import { SelectButton } from 'components/SearchSideBar/SelectButton';
import { ChangeEvent, FC, Fragment } from 'react';
import { SearchCheckboxItem } from 'components/SearchCheckboxList';
import { Text } from '@doda/io-react/common';
import { JobCountQuery } from '@doda/common/src/services/getJobCountAPI';
import { useSelector } from 'react-redux';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { addComma } from '@doda/common/src/utils/convNumberUtil';
import { IndustryDrillDown } from 'pages/DodaFront/View/JobSearchList/components/SearchSideBar/DrillDown/IndustryDrillDown';
import { ConditionsAreaWrapper } from '../../ConditionsAreaWrapper';
import { TitleWithButton } from '../TitleWithButton';
import styles from './index.module.scss';
import { IndustryType } from '../types';

/**
 * Propsの型定義
 * @param industry チェックボックス表示用配列
 * @param updateJobCount 求人件数の更新処理
 * @param onClickOpenModal 業種モーダルの表示処理
 * @param onChangeIsShowOverlay オーバーレイの表示処理
 */
interface Props {
  industry: IndustryType;
  updateJobCount: (
    currentCondition?: JobCountQuery,
    isToggleOccupation?: boolean,
    isToggleIndustry?: boolean
  ) => Promise<void>;
  onClickOpenModal: () => void;
  onChangeIsShowOverlay: () => void;
}

export const IndustryArea: FC<Props> = ({
  industry,
  updateJobCount,
  onClickOpenModal,
  onChangeIsShowOverlay,
}) => {
  const { qc } = useSelector(appQueryParamsSelector);
  const {
    industryDisplayList,
    industryDrillDown,
    updateIsCheckedIndustryLList,
    updateIsCheckedIndustrySList,
    updateDrillDownIsChecked,
  } = industry;

  // 大分類のチェックボックス押下処理
  const onChangeLListCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    selectedId: string
  ) => {
    const checkedList = updateIsCheckedIndustryLList(event, selectedId);
    updateJobCount(
      { industryLId: checkedList, qualificationId: qc },
      false,
      true
    );
    onChangeIsShowOverlay();
  };

  // 小分類のチェックボックス押下処理
  const onChangeSListCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    selectedId: string
  ) => {
    const checkedList = updateIsCheckedIndustrySList(event, selectedId);
    updateJobCount(
      { industrySId: checkedList, qualificationId: qc },
      false,
      true
    );
    onChangeIsShowOverlay();
  };

  // ドリルダウンのチェックボックス押下処理
  const onChangeDrillDownCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    selectedId: string
  ) => {
    if (updateDrillDownIsChecked) {
      const checkedList = updateDrillDownIsChecked(event, selectedId);
      updateJobCount({
        industryLId: checkedList?.industryLId ?? [],
        industrySId: checkedList?.industrySId ?? [],
      });
      onChangeIsShowOverlay();
    }
  };

  return (
    <ConditionsAreaWrapper>
      <TitleWithButton title="業種">
        <SelectButton title="業種を選択" onClick={onClickOpenModal} />
      </TitleWithButton>
      {industryDrillDown ? (
        <IndustryDrillDown
          drillDown={industryDrillDown}
          onChange={onChangeDrillDownCheckbox}
        />
      ) : (
        <div className={styles['industry-check-list__wrapper']}>
          {industryDisplayList.map((industryL) => (
            <Fragment key={industryL.id}>
              {industryL.isDisplay && (
                <SearchCheckboxItem
                  title={industryL.name}
                  value={industryL.id}
                  isChecked={industryL.isChecked}
                  count={industryL.jobCount}
                  onChange={(event) =>
                    onChangeLListCheckbox(event, industryL.id)
                  }
                />
              )}
              {industryL.industrySList.map((industryS, index) => (
                <Fragment key={industryS.id}>
                  {industryS.isDisplay && (
                    <>
                      {/* 大分類が複数表示されてしまうので最初のループの場合のみで表示させる */}
                      {index === 0 && (
                        <div>
                          <p className={styles['industry-check-list__heading']}>
                            {industryL.name}
                            <Text size="12" color="gray" as="span">
                              ({addComma(industryL.jobCount)})
                            </Text>
                          </p>
                        </div>
                      )}
                      <SearchCheckboxItem
                        title={industryS.name}
                        value={industryS.id}
                        isChecked={industryS.isChecked}
                        count={industryS.jobCount}
                        onChange={(event) =>
                          onChangeSListCheckbox(event, industryS.id)
                        }
                      />
                    </>
                  )}
                </Fragment>
              ))}
            </Fragment>
          ))}
        </div>
      )}
    </ConditionsAreaWrapper>
  );
};
